// ─────────────────────────────────────────────────────────────
// BacklogView — all feature cards with filtering and sorting.
// Grouped by status in collapsible sections.
// ─────────────────────────────────────────────────────────────

import { useState, useMemo, useCallback, type MouseEvent } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Collapse,
  IconButton,
  Tooltip,
  Paper,
  Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import type { FeatureCard, CardStatus, Sprint, SprintItemSummary } from "../types";
import { cardsApi, sprintsApi } from "../api/client";
import { useApi } from "../hooks/useApi";
import {
  PriorityBadge,
  TagList,
  SubmittedBy,
} from "../components/CardBadges";
import { CardDetailDialog } from "../components/CardDetailDialog";
import { CreateCardDialog } from "../components/CreateCardDialog";
import { STATUS_COLORS } from "../theme";

type SortField = "priority" | "created_at" | "customer_request_count";

// "In Sprint" cards belong on the Sprint Board — hide them from the backlog.
const STATUS_ORDER: CardStatus[] = ["Candidate", "Ready", "Idea", "Done"];

export function BacklogView() {
  // ── Filters ─────────────────────────────────────────────────
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<CardStatus | "">("");
  const [filterTag, setFilterTag] = useState("");
  const [sortBy, setSortBy] = useState<SortField>("created_at");

  // ── UI state ────────────────────────────────────────────────
  const [selectedCard, setSelectedCard] = useState<FeatureCard | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<CardStatus>>(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // ── Planning sprints ─────────────────────────────────────────
  const { data: planningSprintsData, refetch: refetchPlanningSprints } = useApi(
    () => sprintsApi.list("planning"),
    []
  );
  const planningSprints = planningSprintsData ?? [];

  // ── Data ─────────────────────────────────────────────────────
  const { data, loading, error, refetch } = useApi(
    () =>
      cardsApi.list({
        sort_by: sortBy as "priority" | "value_score" | "created_at" | "customer_request_count",
        order: "desc",
        limit: 500,
      }),
    [sortBy]
  );

  // ── Derived: filter + group ──────────────────────────────────
  const filteredCards = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    return data.cards.filter((card) => {
      // Cards in a sprint or done belong on the Sprint Board / history — never show in backlog
      if (card.status === "In Sprint" || card.status === "Done") return false;
      if (filterStatus && card.status !== filterStatus) return false;
      if (filterTag && !card.tags.some((t) => t.toLowerCase().includes(filterTag.toLowerCase())))
        return false;
      if (q && !card.title.toLowerCase().includes(q) && !(card.description ?? "").toLowerCase().includes(q))
        return false;
      return true;
    });
  }, [data, search, filterStatus, filterTag]);

  const grouped = useMemo(() => {
    const map = new Map<CardStatus, FeatureCard[]>();
    for (const status of STATUS_ORDER) map.set(status, []);
    for (const card of filteredCards) {
      const bucket = map.get(card.status);
      if (bucket) bucket.push(card);
      else {
        // Unknown status — add to Idea bucket
        const idea = map.get("Idea")!;
        idea.push(card);
      }
    }
    return map;
  }, [filteredCards]);

  // ── Handlers ────────────────────────────────────────────────
  const handleCardUpdated = useCallback(
    (updated: FeatureCard) => {
      setSelectedCard(updated);
      refetch();
      refetchPlanningSprints();
    },
    [refetch, refetchPlanningSprints]
  );

  const handleCardDeleted = useCallback(
    (_id: number) => {
      setSelectedCard(null);
      refetch();
    },
    [refetch]
  );

  const toggleGroup = (status: CardStatus) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const handleUpvote = useCallback(
    async (card: FeatureCard, e: MouseEvent) => {
      e.stopPropagation();
      try {
        await cardsApi.upvote(card.id);
        refetch();
      } catch {
        // silently fail
      }
    },
    [refetch]
  );

  /**
   * Handles sprint assignment from the dropdown on each backlog card.
   *
   * - newSprintId === "" means "No sprint" (unassign)
   * - If card is already in a sprint, remove it from the old sprint first
   * - Then add to the new sprint (if a sprint was selected)
   */
  const handleSprintAssign = useCallback(
    async (card: FeatureCard, newSprintId: number | "", e: React.SyntheticEvent) => {
      e.stopPropagation();

      // Resolve the old sprint item for this card (if any)
      // The card list endpoint doesn't include sprint_items — fetch lazily when needed
      let existingItem: SprintItemSummary | undefined;
      if (card.status === "In Sprint") {
        try {
          const detail = await cardsApi.get(card.id);
          existingItem = detail.sprint_items?.[0];
        } catch {
          // If we can't fetch, proceed without removing
        }
      }

      try {
        // Remove from existing sprint if applicable
        if (existingItem) {
          await sprintsApi.removeItem(existingItem.sprint_id, existingItem.id);
        }

        // Add to new sprint if one was selected
        if (newSprintId !== "") {
          const targetSprint = planningSprints.find((s) => s.id === newSprintId);
          await sprintsApi.addItem(newSprintId, {
            card_id: card.id,
            kanban_column: "Candidate Pool",
            priority_in_sprint: 50,
          });
          const sprintLabel = targetSprint ? targetSprint.name : "sprint";
          setSnackbarMessage(`Added "${card.title}" to ${sprintLabel}`);
        } else {
          setSnackbarMessage(`Removed "${card.title}" from sprint`);
        }

        setSnackbarOpen(true);
        refetch();
        refetchPlanningSprints();
      } catch (err) {
        setSnackbarMessage(
          err instanceof Error ? err.message : "Failed to update sprint assignment"
        );
        setSnackbarOpen(true);
      }
    },
    [planningSprints, refetch, refetchPlanningSprints]
  );

  // ── Render ───────────────────────────────────────────────────
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header bar */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h2" sx={{ mr: "auto" }}>
          Backlog
          {data && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              {filteredCards.length} / {data.total} cards
            </Typography>
          )}
        </Typography>

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={refetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          New card
        </Button>
      </Box>

      {/* Filter toolbar */}
      <Box
        sx={{
          px: 3,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <TextField
          placeholder="Search title or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 260 }}
        />

        <Select
          displayEmpty
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as CardStatus | "")}
          renderValue={(val) =>
            val ? val : <em style={{ color: "inherit", opacity: 0.6 }}>All statuses</em>
          }
          size="small"
          sx={{ minWidth: 130 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {STATUS_ORDER.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </Select>

        <TextField
          placeholder="Filter by tag…"
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
          size="small"
          sx={{ width: 160 }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            label="Sort by"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortField)}
          >
            <MenuItem value="priority">Priority</MenuItem>
            <MenuItem value="created_at">Created date</MenuItem>
            <MenuItem value="customer_request_count">Customer requests</MenuItem>
          </Select>
        </FormControl>

        {(search || filterStatus || filterTag) && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setSearch("");
              setFilterStatus("");
              setFilterTag("");
            }}
          >
            Clear filters
          </Button>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load cards: {error}
          </Alert>
        )}

        {!loading && !error && filteredCards.length === 0 && (
          <Box sx={{ textAlign: "center", pt: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No cards match the current filters.
            </Typography>
          </Box>
        )}

        {!loading &&
          !error &&
          STATUS_ORDER.map((status) => {
            const cards = grouped.get(status) ?? [];
            if (cards.length === 0) return null;
            const isCollapsed = collapsedGroups.has(status);
            const accentColor = STATUS_COLORS[status] ?? "#607d8b";

            return (
              <Box key={status} sx={{ mb: 3 }}>
                {/* Group header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleGroup(status)}
                >
                  <Box
                    sx={{
                      width: 3,
                      height: 16,
                      borderRadius: "2px",
                      backgroundColor: accentColor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="overline" sx={{ color: accentColor, letterSpacing: "0.08em" }}>
                    {status}
                  </Typography>
                  <Chip
                    label={cards.length}
                    size="small"
                    sx={{
                      height: "18px",
                      fontSize: "0.65rem",
                      backgroundColor: accentColor,
                      color: "#fff",
                      borderRadius: "3px",
                    }}
                  />
                  <Box sx={{ flex: 1 }} />
                  <IconButton size="small" tabIndex={-1}>
                    {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                  </IconButton>
                </Box>

                <Collapse in={!isCollapsed}>
                  <Paper
                    sx={{
                      overflow: "hidden",
                      borderRadius: "4px",
                    }}
                  >
                    {cards.map((card, idx) => (
                      <Box key={card.id}>
                        {idx > 0 && <Divider />}
                        <BacklogCardRow
                          card={card}
                          planningSprints={planningSprints}
                          onClick={() => setSelectedCard(card)}
                          onUpvote={(e) => handleUpvote(card, e)}
                          onSprintAssign={(sprintId, e) => void handleSprintAssign(card, sprintId, e)}
                        />
                      </Box>
                    ))}
                  </Paper>
                </Collapse>
              </Box>
            );
          })}
      </Box>

      {/* Dialogs */}
      {selectedCard && (
        <CardDetailDialog
          card={selectedCard}
          open={Boolean(selectedCard)}
          onClose={() => setSelectedCard(null)}
          onUpdated={handleCardUpdated}
          onDeleted={handleCardDeleted}
        />
      )}

      <CreateCardDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refetch}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

// ── BacklogCardRow ────────────────────────────────────────────

interface BacklogCardRowProps {
  card: FeatureCard;
  planningSprints: Sprint[];
  onClick: () => void;
  onUpvote: (e: MouseEvent) => void;
  onSprintAssign: (sprintId: number | "", e: React.SyntheticEvent) => void;
}

/**
 * Determine the current sprint assignment value for the dropdown.
 *
 * - Cards with status "In Sprint" that have a sprint_items entry: return that sprint_id.
 *   (The list endpoint doesn't populate sprint_items, so this falls back to "" until
 *   the user opens the dropdown for an "In Sprint" card — the parent handles lazy fetch.)
 * - All other cards: return "".
 */
function currentSprintValue(card: FeatureCard): number | "" {
  if (card.status === "In Sprint" && card.sprint_items && card.sprint_items.length > 0) {
    return card.sprint_items[0].sprint_id;
  }
  return "";
}

function BacklogCardRow({ card, planningSprints, onClick, onUpvote, onSprintAssign }: BacklogCardRowProps) {
  // Track whether the user has opened the dropdown for an "In Sprint" card so we
  // can show a loading indicator while fetching the lazy card detail.
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [resolvedSprintItems, setResolvedSprintItems] = useState<SprintItemSummary[] | null>(null);

  // A card is "in active sprint" if its sprint is not in the planning list.
  // We infer this by checking if the current sprint_id (if known) is absent from planningSprints.
  const sprintItems = resolvedSprintItems ?? card.sprint_items ?? [];
  const currentSprintId: number | "" =
    sprintItems.length > 0 ? sprintItems[0].sprint_id : currentSprintValue(card);

  // Is the card locked because it's in an active (non-planning) sprint?
  const currentSprintIsActive =
    card.status === "In Sprint" &&
    currentSprintId !== "" &&
    !planningSprints.some((s) => s.id === currentSprintId);

  const noPlanningSprints = planningSprints.length === 0;

  // Determine dropdown disabled state
  const dropdownDisabled = currentSprintIsActive || (noPlanningSprints && card.status !== "In Sprint");

  // Tooltip text for disabled state
  let disabledTooltip = "";
  if (currentSprintIsActive) {
    disabledTooltip = "Sprint is queued — cannot reassign";
  } else if (noPlanningSprints) {
    disabledTooltip = "Create a sprint in the Board view first.";
  }

  const handleDropdownOpen = async () => {
    setDropdownOpen(true);
    // Lazily fetch sprint detail for "In Sprint" cards that don't have sprint_items yet
    if (card.status === "In Sprint" && sprintItems.length === 0 && !loadingDetail) {
      setLoadingDetail(true);
      try {
        const detail = await cardsApi.get(card.id);
        setResolvedSprintItems(detail.sprint_items ?? []);
      } catch {
        // If fetch fails, we proceed with empty sprint_items
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  return (
    <Box
      onClick={onClick}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      sx={{
        display: "flex",
        flexDirection: "column",
        px: 2,
        py: 1.5,
        cursor: "pointer",
        transition: "background-color 150ms",
        "&:hover": { backgroundColor: "action.hover" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: "-2px",
        },
      }}
    >
      {/* Row 1: title (left) + priority badge + upvote (right) */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 500, flex: 1, minWidth: 0, overflow: "hidden" }}
        >
          {card.title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          <PriorityBadge priority={card.priority ?? "Medium"} />
          <SubmittedBy name={card.submitted_by} />
          <Tooltip title="Upvote">
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
              onClick={(e) => {
                e.stopPropagation();
                onUpvote(e);
              }}
            >
              <ThumbUpOutlinedIcon sx={{ fontSize: "0.875rem" }} />
              <Typography variant="caption">{card.upvotes}</Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* Row 2: tags (left, can grow) + sprint dropdown (right, fixed width) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 0.5,
          minWidth: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          {card.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {card.description}
            </Typography>
          )}
          {card.tags.length > 0 && (
            <Box sx={{ mt: card.description ? 0.25 : 0 }}>
              <TagList tags={card.tags} max={2} />
            </Box>
          )}
        </Box>

        {/* Sprint assignment dropdown */}
        <Tooltip
          title={dropdownDisabled ? disabledTooltip : ""}
          disableHoverListener={!dropdownDisabled}
        >
          <span onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
            <FormControl
              size="small"
              disabled={dropdownDisabled}
              sx={{ minWidth: 148 }}
            >
              <Select
                displayEmpty
                open={dropdownOpen}
                onOpen={() => { void handleDropdownOpen(); }}
                onClose={() => setDropdownOpen(false)}
                value={currentSprintId}
                onChange={(e) => {
                  const val = e.target.value as number | "";
                  onSprintAssign(val, e as unknown as React.SyntheticEvent);
                }}
                renderValue={(val) => {
                  if (loadingDetail) return <em style={{ fontSize: "0.75rem" }}>Loading…</em>;
                  // val is "" (empty string) when nothing is selected
                  if (!val) {
                    if (noPlanningSprints) return <em style={{ fontSize: "0.75rem" }}>No planning sprints</em>;
                    return <em style={{ fontSize: "0.75rem" }}>Assign to sprint…</em>;
                  }
                  const sprintId = val as unknown as number;
                  const sprint = planningSprints.find((s) => s.id === sprintId);
                  if (!sprint) {
                    // Sprint not in planning list — it's queued/completed
                    return <em style={{ fontSize: "0.75rem" }}>Sprint (locked)</em>;
                  }
                  return (
                    <span style={{ fontSize: "0.75rem" }}>
                      {sprint.name.length > 22 ? `${sprint.name.slice(0, 22)}…` : sprint.name}
                    </span>
                  );
                }}
                sx={{
                  fontSize: "0.75rem",
                  "& .MuiSelect-select": { py: "3px", px: "8px" },
                }}
                MenuProps={{ disablePortal: false }}
              >
                <MenuItem value="" sx={{ fontSize: "0.8rem" }}>
                  <em>No sprint</em>
                </MenuItem>
                {planningSprints.map((sprint) => (
                  <MenuItem key={sprint.id} value={sprint.id} sx={{ fontSize: "0.8rem" }}>
                    {sprint.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
